import * as Clipboard from "clipboard";
import { Form, FormikProps, withFormik } from "formik";
import * as Cookies from "js-cookie";
import { transparentize, invert } from "polished";
import * as queryString from "query-string";
import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 } from "uuid";
import * as Yup from "yup";
import { identify, track } from "../../analytics";
import styled from "../../styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook, faTwitter } from "@fortawesome/free-brands-svg-icons";
import { media } from "../../breakpoints";
import ITheme from "../../theme";

// todo: replace this with a more modern package
// tslint:disable-next-line
const jsonp = require("jsonp");

const StyledContainer = styled.div`
  ${media.TABLET`
    width: 100%;
  `};
`;

const BodyText = styled.p`
  font-size: 17px;
  color: ${props => transparentize(0.5, props.theme.colors.trueblack)};
  margin-top: 20px;
  margin-bottom: 0;
  margin-left: 13px;

  ${media.TABLET`
    text-align: center;
  `};
`;

const StyledForm = styled(Form)<{
  backgroundColor: string;
}>`
  align-items: center;
  border: 1px solid
    ${({ backgroundColor, theme }) =>
      backgroundColor
        ? backgroundColor
        : transparentize(0.25, theme.colors.blue)};
  border-radius: 3px;
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  padding: 5px;

  ${media.TABLET`
    border: none;
    flex-direction: column;
    margin-top: 30px;
    padding: 0;
    min-width: 300px;
    width: 100%;
  `};
`;

const Input = styled.input<{
  backgroundColor: string;
}>`
  background: transparent;
  border: none;
  box-shadow: none;
  color: ${props =>
    invert(
      props.backgroundColor ? props.backgroundColor : props.theme.colors.white
    )};
  flex: 1;
  font-size: 16px;
  height: 33px;
  padding: 0 5px;
  transition: border 0.25s ease, color 0.25s ease;
  -webkit-appearance: none;
  -moz-appearance: none;

  ${media.TABLET`
    border: 1px solid ${(props: { theme: ITheme; backgroundColor: string }) =>
      props.backgroundColor ? props.backgroundColor : props.theme.colors.blue};
    border-radius: 3px;
    height: 100%;
    padding: 13px 10px;
    width: 100%;
  `};
`;

const Button = styled.button<{ isInFooter: boolean; backgroundColor: string }>`
  background-color: ${({ backgroundColor, theme }) =>
    backgroundColor
      ? backgroundColor
      : transparentize(0.25, theme.colors.blue)};
  border: none;
  border-radius: 3px;
  color: ${props => props.theme.colors.white};
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  height: 33px;
  width: 180px;

  ${media.TABLET`
    height: 45px;
    width: 100%;
    margin-top: ${(props: { isInFooter: boolean }) =>
      props.isInFooter ? "15px" : "5px"};
  `};
`;

const Headline = styled.p`
  color: ${props => props.theme.colors.trueblack};
  font-size: 30px;
  margin-bottom: 0;
  margin-top: 70px;
  margin-left: 13px;

  ${media.TABLET`
    margin-top: 40px;
    text-align: center;
  `};
`;

const SocialLinks = styled.div`
  align-items: center;
  display: flex;
  margin-top: 35px;

  svg {
    margin-right: 20px;

    ${media.TABLET`
      margin: 0 10px;
    `};
  }

  ${media.TABLET`
    justify-content: center;
  `};
`;

const ReferralCode = styled.p`
  color: ${props => props.theme.colors.black};
  font-size: 16px;
  margin-bottom: 0;
  margin-top: 25px;
  margin-left: 13px;

  &:hover {
    cursor: pointer;
  }

  ${media.TABLET`
    text-align: center;
  `};
`;

interface IInnerWaitlistSignUpForm {
  broker: string;
  email: string;
  referrerCode?: string | null;
  referralCode: string;
  isSubmitted: boolean;
  isInFooter?: boolean;
}

interface IInnerUI {
  hideSection?: boolean;
  isInFooter?: boolean;
  backgroundColor?: string;
}

class InnerWaitlistSignUpForm extends React.Component<
  FormikProps<IInnerWaitlistSignUpForm> & IInnerUI & RouteComponentProps<{}>
> {
  public constructor(
    props: FormikProps<IInnerWaitlistSignUpForm> & RouteComponentProps<{}>
  ) {
    super(props);
    this.handleFacebookClick = this.handleFacebookClick.bind(this);
    this.handleTwitterClick = this.handleTwitterClick.bind(this);
  }

  public componentDidMount() {
    // tslint:disable-next-line
    new Clipboard("#copy");

    const queryObject = queryString.parse(this.props.history.location.search);
    const { email, broker } = queryObject;

    if (broker) {
      Cookies.set("broker", broker);
    }

    this.props.setFieldValue("email", email);

    if (Cookies.get("broker")) {
      this.props.setFieldValue("broker", Cookies.get("broker"));
    } else {
      this.props.setFieldValue("broker", "unspecified");
    }
  }

  public handleFacebookClick(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault();
    track("Social Link Clicked", { platform: "facebook" });

    // tslint:disable-next-line
    window["FB"].ui({
      href: "https://saytechnologies.com/",
      method: "share",
      quote:
        "Make your shares do something for a change. Join the Say beta today."
    });
  }

  public handleTwitterClick(event: React.MouseEvent<HTMLElement>) {
    track("Social Link Clicked", { platform: "twitter" });
  }

  public twitterLink() {
    const tweet = encodeURI(
      "Make your shares do something for a change. Join the @say beta today."
    );
    const referralUrl = `https://saytechnologies.com/r/${this.props.values.referralCode}`;
    return `https://twitter.com/intent/tweet?text=${tweet}&url=${referralUrl}`;
  }

  public render() {
    const {
      values,
      errors,
      touched,
      isValid,
      handleChange,
      handleBlur,
      isSubmitting
    } = this.props;

    const shareUrl = `https://saytechnologies.com/r/${values.referralCode}`;
    return (
      <StyledContainer>
        {!values.isSubmitted ? (
          <div>
            <StyledForm
              backgroundColor={
                this.props.backgroundColor === undefined
                  ? ""
                  : this.props.backgroundColor
              }
            >
              <Input
                type="email"
                name="email"
                aria-label="sign-up"
                placeholder="e.g., email@example.com"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                backgroundColor={
                  this.props.backgroundColor === undefined
                    ? ""
                    : this.props.backgroundColor
                }
                className={
                  errors.email && touched.email
                    ? "error"
                    : "" + isValid && touched.email
                    ? "valid"
                    : ""
                }
                autoCapitalize="off"
                autoCorrect="off"
              />

              <Button
                type="submit"
                disabled={isSubmitting || !isValid}
                isInFooter={
                  this.props.isInFooter === undefined
                    ? false
                    : this.props.isInFooter
                }
                backgroundColor={
                  this.props.backgroundColor === undefined
                    ? ""
                    : this.props.backgroundColor
                }
              >
                {isSubmitting ? "Submitting…" : "Get Early Access"}
              </Button>
            </StyledForm>
          </div>
        ) : (
          <div>
            <Headline>You're on the waitlist.</Headline>
            <BodyText>
              Say works better with friends. Invite them with your link below.
            </BodyText>
            <SocialLinks>
              <div>
                <a onClick={this.handleFacebookClick}>
                  <FontAwesomeIcon icon={faFacebook} color="#fff" size="2x" />
                </a>
              </div>
              <div>
                <a onClick={this.handleTwitterClick} href={this.twitterLink()}>
                  <FontAwesomeIcon icon={faTwitter} color="#fff" size="2x" />
                </a>
              </div>
            </SocialLinks>

            <ReferralCode data-clipboard-text={shareUrl}>
              saytechnologies.com/r/
              {values.referralCode}
            </ReferralCode>
          </div>
        )}
      </StyledContainer>
    );
  }
}

interface IWaitlistSignUpForm {
  initialBroker?: string;
  initialEmail?: string;
  initialReferrerCode?: string;
  hideSection?: boolean;
  isInFooter?: boolean;
  backgroundColor?: string;
}

const WaitlistSignUpForm = withFormik<
  IWaitlistSignUpForm,
  IInnerWaitlistSignUpForm
>({
  handleSubmit: (values, { setSubmitting, setFieldValue, resetForm }) => {
    const uuid = v4();
    const referralCode = uuid.substr(uuid.length - 5);
    setFieldValue("referralCode", referralCode);

    const params = {
      BROKER: values.broker,
      EMAIL: values.email,
      REFERRALCD: referralCode,
      REFERRERCD: values.referrerCode,
      UTMMEDIUM: "web",
      UTMSOURCE: "saycom",
      b_87df7a4c1ed90dd238c0b4791_6b31d3f95f: "",
      id: "6b31d3f95f",
      subscribe: "Subscribe",
      u: "87df7a4c1ed90dd238c0b4791"
    };

    const url = `https://say.us17.list-manage.com/subscribe/post-json?${queryString.stringify(
      params
    )}`;

    identify({ email: values.email, broker: values.broker });

    // tslint:disable-next-line
    jsonp(url, { param: "c" }, (err: any, data: any) => {
      if (err) {
        track(
          `${values.isInFooter ? "Footer Click: " : ""}Sign Up for Waitlist`,
          { status: "failed" }
        );
        setSubmitting(false);

        return toast.error(err.message, {
          position: toast.POSITION.TOP_RIGHT
        });
      } else {
        track(
          `${values.isInFooter ? "Footer Click: " : ""}Sign Up for Waitlist`,
          {
            status: "success"
          }
        );
        setSubmitting(false);
        if (data.result === "error") {
          track("Sign Up for Waitlist", { status: "failed" });
          if (data.msg.indexOf("already subscribed")) {
            return toast.error(`${values.email} is already subscribed.`, {
              position: toast.POSITION.TOP_RIGHT
            });
          }
          return toast.error(data.msg, {
            position: toast.POSITION.TOP_RIGHT
          });
        }

        return setFieldValue("isSubmitted", true);
      }
    });
  },
  mapPropsToValues: props => {
    return {
      broker: props.initialBroker || "",
      email: props.initialEmail || "",
      isSubmitted: false,
      hideSection: props.hideSection,
      backgroundColor: props.backgroundColor,
      isInFooter: props.isInFooter,
      referralCode: "",
      referrerCode: props.initialReferrerCode || ""
    };
  },
  validationSchema: Yup.object().shape({
    broker: Yup.string().required("Please select a broker."),
    email: Yup.string()
      .email("Please enter a valid email address.")
      .required("Email address is required")
  })
})(withRouter(InnerWaitlistSignUpForm));

export default WaitlistSignUpForm;



// WEBPACK FOOTER //
// ./src/components/WaitlistSignUpForm/WaitlistSignUpForm.tsx